/**
 * Advanced Asset Loader Example
 * This file demonstrates more complex usage patterns of the AssetLoader
 */

import AssetLoader from './AssetLoader.js'

class AssetManager {
    constructor(scene) {
        this.scene = scene
        this.assetLoader = AssetLoader.getInstance()
        this.loadedModels = new Map()
        this.loadedTextures = new Map()
        this.configurations = new Map()
    }

    /**
     * Load a complete level/scene
     */
    async loadLevel(levelName) {
        const levelAssets = [
            `/assets/levels/${levelName}/models.json`,
            `/assets/levels/${levelName}/textures.json`,
            `/assets/levels/${levelName}/config.json`
        ]

        const promises = levelAssets.map(assetFile => 
            new Promise((resolve) => {
                this.assetLoader.loadAssetsFromFile(assetFile, resolve)
            })
        )

        try {
            const results = await Promise.all(promises)
            console.log(`Level ${levelName} loaded successfully`, results)
            return this.processLevelAssets(results)
        } catch (error) {
            console.error(`Failed to load level ${levelName}:`, error)
            return null
        }
    }

    /**
     * Process loaded level assets
     */
    processLevelAssets(assetGroups) {
        const processedAssets = {
            models: [],
            textures: [],
            config: null
        }

        assetGroups.forEach(assets => {
            Object.entries(assets).forEach(([key, asset]) => {
                if (asset.scene) {
                    // GLTF Model
                    processedAssets.models.push({
                        key,
                        model: asset.scene.clone()
                    })
                    this.loadedModels.set(key, asset)
                } else if (asset.isTexture) {
                    // Texture
                    processedAssets.textures.push({
                        key,
                        texture: asset
                    })
                    this.loadedTextures.set(key, asset)
                } else if (typeof asset === 'object') {
                    // Configuration
                    processedAssets.config = asset
                    this.configurations.set(key, asset)
                }
            })
        })

        return processedAssets
    }

    /**
     * Preload assets for smooth gameplay
     */
    preloadAssets(assetList) {
        const promises = assetList.map(assetPath => 
            new Promise((resolve, reject) => {
                this.assetLoader.loadAssetsFromFile(
                    assetPath, 
                    resolve, 
                    (progress) => {
                        console.log(`Preloading ${assetPath}: ${Math.round(progress * 100)}%`)
                    }
                )
            })
        )

        return Promise.all(promises)
    }

    /**
     * Get asset with fallback
     */
    getAssetSafe(key, fallback = null) {
        return this.assetLoader.getAsset(key) || fallback
    }

    /**
     * Clean up unused assets
     */
    cleanupUnusedAssets(keepList = []) {
        const allAssets = this.assetLoader.getAllAssets()
        
        allAssets.forEach((asset, key) => {
            if (!keepList.includes(key)) {
                this.assetLoader.removeAsset(key)
                console.log(`Cleaned up asset: ${key}`)
            }
        })
    }
}

export default AssetManager
